import React, { useEffect, useState } from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import * as Sentry from '@sentry/react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import { useAtom } from 'jotai';
import { debounce } from 'lodash';
import { Outlet } from 'react-router-dom';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { CssBaseline } from '@material-ui/core';
import { themeWithNewFont as MDSv1ThemeWithNewFont } from '@marqvision/shared/src/library';
import { getDeviceMetrics, monitorNetwork, useFavicon } from '@marqvision/shared';
import { MDSSnackbarContainer, MDSThemeProvider, _MDSThemeValue } from '@marqvision/mds-v2';
import pkg from '../package.json';
import './App.css';
import { LanguageEnum } from './LocalizedStrings';
import { checkIsClientView, useAuth, useUserInfoQuery } from './apis/auth';
import { addAlertAtom, removeAlertByTypeAtom } from './atoms';
import { LanguageLocalStorageIdentifier, useLogger } from './hooks';
import { ErrorBoundaryView } from './pages/ErrorPage/ErrorBoundary';

dayjs.extend(isToday);

export interface ILanguageContext {
  language: LanguageEnum;
  setLanguage: React.Dispatch<React.SetStateAction<LanguageEnum>>;
}

export const LanguageContext = React.createContext<ILanguageContext>({
  language: LanguageEnum.English,
  setLanguage: () => {},
});

export const growthbook = new GrowthBook({
  apiHost: import.meta.env.REACT_APP_GROWTHBOOK,
  clientKey: import.meta.env.REACT_APP_GROWTHBOOK_KEY,
  enableDevMode: true,
  trackingCallback: (experiment, result) => {
    // TODO: Use your real analytics tracking system
    console.log('### Viewed Experiment', {
      experimentId: experiment.key,
      variationId: result.variationId,
    });
  },
});

monitorNetwork((res) => {
  if (res.status !== 200) {
    Sentry.captureMessage(`[debug][PROD-11725] ${res.status} ${res.url}`);
  }
});

const USE_REACT_QUERY_DEVTOOLS =
  import.meta.env.MODE === 'development' && import.meta.env.REACT_APP_REACT_QUERY_DEVTOOLS === 'true';

const App = (): JSX.Element => {
  const [_, addAlert] = useAtom(addAlertAtom);
  const [__, removeAlert] = useAtom(removeAlertByTypeAtom);
  const [language, setLanguage] = useState<LanguageEnum>(
    (() => {
      /**
       * 초기 언어 우선순위
       * 1. Session Storage 에 저장 되어있는 언어
       * 2. 브라우저 내장 객체의 언어
       * 3. 영어
       */
      const initialLanguage =
        (sessionStorage.getItem(LanguageLocalStorageIdentifier) as LanguageEnum) ||
        (typeof navigator.language === 'string' && (navigator.language.substring(0, 2) as LanguageEnum)) ||
        LanguageEnum.English;

      return initialLanguage;
    })()
  );

  useEffect(() => {
    sessionStorage.setItem(LanguageLocalStorageIdentifier, language);
  }, [language]);

  useEffect(() => {
    // crash logging
    if (sessionStorage.getItem('good_exit') && sessionStorage.getItem('good_exit') !== 'true') {
      Sentry.getCurrentScope().setExtras({
        time_before_crash: sessionStorage.getItem('time_before_crash'),
        js_heap_size_limit: sessionStorage.getItem('js_heap_size_limit'),
        total_js_heap_size: sessionStorage.getItem('total_js_heap_size'),
        used_js_heap_size: sessionStorage.getItem('used_js_heap_size'),
      });
      Sentry.captureMessage('crash detected');
    }
    Sentry.setContext('device', {
      ...getDeviceMetrics(),
    });
    // Load features asynchronously when the app renders
    growthbook.init({ streaming: true }).then(() => {
      const isAmplitudeEnabled = growthbook.getFeatureValue('brand-client-8124-amplitude', false);
      setIsAmplitudeEnabled(isAmplitudeEnabled);
    });
  }, []);

  useFavicon('client');

  const { data } = useAuth();
  const { data: userInfo } = useUserInfoQuery();
  const { amplitudeLogging } = useLogger();
  const isLogin = !!data?.access_token;

  const [isAmplitudeEnabled, setIsAmplitudeEnabled] = useState(false);
  useEffect(() => {
    if (isLogin) {
      /**
       * note-@jamie: [PROD-12926] apis/auth/index.ts에 isClientView가 있는데 왜 여기에서 isFromAdmin를 또 선언하는가?
       *
       * TL;DR: React router 타기 전애 앱이 시작되자마자 clientView 여부를 알아야한다 - 앱이 처음 실행되는 시점에 amplitude script 실행 분기를 타야해서!
       *
       * 1. apis/auth/index.ts 에서 isClientView 값이 설정되는 시점은, router에서 Login과 관련 된 곳으로 이동한 뒤.
       *    -> 즉, 앱이 처음 실행되자마자 실행되는 코드가 아니라, 나중에 실행되는 코드임.
       * 2. 그러나, amplitude script 실행 분기는 앱이 처음 실행되자마자 실행되어야 하는 로직임.
       *   - 이 로직 자체를 로그인 뒤로 옮길 수도 있지만, 진입 주소가 케바케라 모든 페이지의 엔트리 포인트인 App에서 한번에 처리하는게 효율적임.
       * 3. isClientView 값을 같이 보는 이유는,
       *   - client view로 랜딩한 이후에는 url에서 'dashboard' 가 사라지기 때문에 url을 기준으로 client view 여부를 판단할 수 없고
       *   - client view로 랜딩한 상태에서 새로고침을 url에서는 dashboard가 사라진 상태라는 건 마찬가지임.
       *   - 그 때 쯤에는 sessionStorage에 토큰이 저장되어있기 때문에 isClientView 값이 의도 대로 설정 완료된 상태임.
       */
      const sessionReplayTracking = sessionReplayPlugin(); // remote config를 적용하기 위해 인자는 비워둔다
      const isFromAdmin =
        window.location.href.includes('/dashboard/') || sessionStorage.getItem('_marq_cc_ui') !== null;
      isFromAdmin ? amplitude.remove(sessionReplayTracking.name as string) : amplitude.add(sessionReplayTracking);

      amplitude.init(import.meta.env.REACT_APP_AMPLITUDE_API_KEY as string, {
        defaultTracking: false,
        fetchRemoteConfig: true,
        autocapture: {
          elementInteractions: false,
        },
        appVersion: pkg.version,
        // logLevel: import.meta.env.REACT_APP_ENV === 'development' ? 4 : undefined,
        logLevel:
          localStorage.getItem('_marq_cc_ui_amplitudeLogLevel') && isAmplitudeEnabled
            ? parseInt(localStorage.getItem('_marq_cc_ui_amplitudeLogLevel') as string)
            : undefined,
      });
    }
  }, [isAmplitudeEnabled, isLogin]);

  useEffect(() => {
    if (userInfo?.unpaid_count && userInfo.unpaid_count > 0) {
      addAlert({
        type: 'unpaid',
        count: userInfo.unpaid_count,
        excludedRoutes: [
          '/settings/billing', // 결제 설정 페이지에서는 표시하지 않음
        ],
      });
    } else {
      removeAlert('unpaid');
    }
  }, [addAlert, removeAlert, userInfo]);

  useEffect(() => {
    const handleResize = () => {
      if (isLogin && isAmplitudeEnabled) {
        amplitudeLogging('resize');
      }
    };

    const debouncedHandleResize = debounce(handleResize, 250); // 250ms debounce

    window.addEventListener('resize', debouncedHandleResize);
    return () => window.removeEventListener('resize', debouncedHandleResize);
  }, [isLogin, isAmplitudeEnabled, amplitudeLogging]);

  // note-@suwan: 유저의 다크모드 사용 여부를 트래킹 하기 위해 user property에 관련 속성 적용
  // https://marqvision.atlassian.net/browse/PROD-16321 참고
  useEffect(() => {
    if (isLogin && isAmplitudeEnabled) {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const identifyObj = new amplitude.Identify();

      const isClientView = checkIsClientView();

      identifyObj.set('custom_system_dark_mode', prefersDark);
      identifyObj.set('custom_user_by', isClientView ? 'admin' : 'client');

      amplitude.identify(identifyObj);
    }
  }, [isLogin, isAmplitudeEnabled]);

  return (
    <GrowthBookProvider growthbook={growthbook}>
      <Sentry.ErrorBoundary fallback={(errorData) => <ErrorBoundaryView errorData={errorData} />}>
        <MDSThemeProvider>
          <MuiThemeProvider theme={{ ...MDSv1ThemeWithNewFont, ..._MDSThemeValue }}>
            <LanguageContext.Provider value={{ language, setLanguage }}>
              <CssBaseline />
              <Outlet />
              <MDSSnackbarContainer />
            </LanguageContext.Provider>
          </MuiThemeProvider>
        </MDSThemeProvider>

        {USE_REACT_QUERY_DEVTOOLS && <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />}
      </Sentry.ErrorBoundary>
    </GrowthBookProvider>
  );
};

export default App;
